import { supabase } from "@/lib/supabaseClient";
import type { Word } from "@/types/models";

const PAGE_SIZE = 1000;

/** 단어 검색 결과 (word_sets 정보 포함) */
export type SearchWordRow = Word & {
  word_sets: { id: string; title: string; is_free: boolean } | null;
};

const SEARCH_PAGE_SIZE = 30;

/** 단어/뜻 앞부분 일치 검색 (word_sets 조인, 오프셋 페이지네이션) */
export async function searchWords(query: string, offset = 0): Promise<SearchWordRow[]> {
  const pattern = `${query}%`;

  const { data, error } = await supabase
    .from("words")
    .select("*, word_sets!inner(id, title, is_free)")
    .or(`word.ilike.${pattern},meaning.ilike.${pattern}`)
    .order("word")
    .range(offset, offset + SEARCH_PAGE_SIZE - 1);

  if (error) {
    throw error;
  }

  return data as SearchWordRow[];
}

export { SEARCH_PAGE_SIZE };

/** 특정 세트의 단어 조회 */
export async function getWordsBySetId(setId: string): Promise<Word[]> {
  const allData: Word[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("words")
      .select("*")
      .eq("set_id", setId)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw error;
    }

    allData.push(...data);
    if (data.length < PAGE_SIZE) {
      break;
    }
    from += PAGE_SIZE;
  }

  return allData;
}

/** 여러 세트의 단어 조회 */
export async function getWordsBySetIds(setIds: string[]): Promise<Word[]> {
  const allData: Word[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("words")
      .select("*")
      .in("set_id", setIds)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw error;
    }

    allData.push(...data);
    if (data.length < PAGE_SIZE) {
      break;
    }
    from += PAGE_SIZE;
  }

  return allData;
}

/** 즐겨찾기된 단어 조회 (PAGE_SIZE 초과 대비 페이지네이션) */
export async function getFavoriteWords(userId: string): Promise<Word[]> {
  const allData: Word[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("user_favorites")
      .select("word_id, words(*)")
      .eq("user_id", userId)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw error;
    }

    const words = data.map(row => row.words).filter(Boolean) as unknown as Word[];
    allData.push(...words);
    if (data.length < PAGE_SIZE) {
      break;
    }
    from += PAGE_SIZE;
  }

  return allData;
}
